/**
 * God Mode Entity Inspection Utility
 *
 * Takes any DB entity record and returns a structured array of
 * { label, value, category } for display in the GodModePanel.
 *
 * Categories: 'identity', 'status', 'metrics', 'timestamps', 'config', 'raw'
 */

// ─── Formatters ────────────────────────────────────────────

function formatValue(val) {
  if (val === null || val === undefined) return '—';
  if (val === true) return '✓ true';
  if (val === false) return '✗ false';
  if (typeof val === 'object' && !Array.isArray(val)) return JSON.stringify(val, null, 2);
  if (Array.isArray(val)) return val.length === 0 ? '[]' : JSON.stringify(val);
  return String(val);
}

function truncateId(id) {
  if (!id || typeof id !== 'string') return '—';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    let relative;
    if (diffMin < 1) relative = 'just now';
    else if (diffMin < 60) relative = `${diffMin}m ago`;
    else if (diffHr < 24) relative = `${diffHr}h ago`;
    else if (diffDay < 30) relative = `${diffDay}d ago`;
    else relative = date.toLocaleDateString();

    return `${relative} · ${date.toISOString().replace('T', ' ').slice(0, 19)}`;
  } catch {
    return dateStr;
  }
}

// ─── Entity Inspectors ─────────────────────────────────────

function inspectAnnotator(record) {
  return [
    { label: 'ID', value: record.id, category: 'identity', copyable: true },
    { label: 'Name', value: record.name, category: 'identity' },
    { label: 'Email', value: record.email, category: 'identity' },
    { label: 'Role', value: record.role || 'annotator', category: 'identity' },
    { label: 'Org ID', value: truncateId(record.org_id), category: 'identity', copyable: true, fullValue: record.org_id },

    { label: 'Status', value: record.status, category: 'status' },
    { label: 'Tier', value: record.tier, category: 'status' },
    { label: 'Career Level', value: record.career_level, category: 'status' },
    { label: 'Domain', value: record.primary_domain, category: 'status' },
    { label: 'Secondary Domains', value: formatValue(record.secondary_domains), category: 'status' },
    { label: 'Consent', value: record.consent_status, category: 'status' },
    { label: 'Environment', value: record.environment, category: 'status' },

    { label: 'Quality Score', value: record.quality_score, category: 'metrics' },
    { label: 'Craft Score', value: record.craft_score_overall, category: 'metrics' },
    { label: 'Precision', value: record.craft_score_precision, category: 'metrics' },
    { label: 'Consistency', value: record.craft_score_consistency, category: 'metrics' },
    { label: 'Edge Cases', value: record.craft_score_edge_cases, category: 'metrics' },
    { label: 'Knowledge', value: record.craft_score_knowledge, category: 'metrics' },
    { label: 'Streak', value: record.streak, category: 'metrics' },
    { label: 'Total Projects', value: record.total_jobs_completed, category: 'metrics' },
    { label: 'Total Hours', value: record.total_hours_worked, category: 'metrics' },
    { label: 'Lifetime Earnings', value: record.lifetime_earnings ? `$${record.lifetime_earnings}` : '—', category: 'metrics' },

    { label: 'Created', value: formatDate(record.created_at), category: 'timestamps' },
    { label: 'Last Active', value: formatDate(record.last_active_at), category: 'timestamps' },
    { label: 'Last Login', value: formatDate(record.last_login_at), category: 'timestamps' },
    { label: 'Onboarding Date', value: formatDate(record.onboarding_date), category: 'timestamps' },

    { label: 'External IDs', value: formatValue(record.external_ids), category: 'config' },
    { label: 'Google ID', value: truncateId(record.google_id), category: 'config' },
    { label: 'Insightful Status', value: record.insightful_onboarding_status, category: 'config' },
    { label: 'Deel Status', value: record.deel_onboarding_status, category: 'config' },
  ].filter(f => f.value !== '—' && f.value !== null && f.value !== undefined);
}

function inspectJob(record) {
  return [
    { label: 'ID', value: record.id, category: 'identity', copyable: true },
    { label: 'Title', value: record.title, category: 'identity' },
    { label: 'Client', value: record.client_display_name || record.client_name, category: 'identity' },
    { label: 'Client ID', value: truncateId(record.client_id), category: 'identity', copyable: true, fullValue: record.client_id },
    { label: 'Org ID', value: truncateId(record.org_id), category: 'identity', copyable: true, fullValue: record.org_id },

    { label: 'Status', value: record.status, category: 'status' },
    { label: 'Domain', value: record.domain, category: 'status' },
    { label: 'Tier', value: record.tier, category: 'status' },
    { label: 'Environment', value: record.environment, category: 'status' },
    { label: 'Staffing Type', value: record.staffing_type, category: 'status' },

    { label: 'Base Rate', value: record.base_rate ? `$${record.base_rate}/hr` : '—', category: 'metrics' },
    { label: 'Rate Range', value: record.rate_min && record.rate_max ? `$${record.rate_min}-$${record.rate_max}` : '—', category: 'metrics' },
    { label: 'Team Size', value: record.team_size, category: 'metrics' },
    { label: 'Current Staffed', value: record.current_staffed, category: 'metrics' },
    { label: 'Budget', value: record.budget ? `$${record.budget}` : '—', category: 'metrics' },
    { label: 'Quality Avg', value: record.quality_average, category: 'metrics' },
    { label: 'Completion', value: record.completion_percentage ? `${record.completion_percentage}%` : '—', category: 'metrics' },
    { label: 'Annotations', value: `${record.annotations_completed || 0} / ${record.annotations_target || '—'}`, category: 'metrics' },
    { label: 'Hours Logged', value: record.hours_logged_total, category: 'metrics' },
    { label: 'Referral Bonus', value: record.referral_bonus ? `$${record.referral_bonus}` : '—', category: 'metrics' },

    { label: 'Created', value: formatDate(record.created_at), category: 'timestamps' },
    { label: 'Published', value: formatDate(record.published_at), category: 'timestamps' },
    { label: 'Start Date', value: formatDate(record.start_date), category: 'timestamps' },
    { label: 'End Date', value: formatDate(record.end_date), category: 'timestamps' },

    { label: 'Uses Pipelines', value: formatValue(record.uses_pipelines), category: 'config' },
    { label: 'Instant Match', value: formatValue(record.instant_match_enabled), category: 'config' },
    { label: 'Hours Tracking', value: record.hours_tracking_method, category: 'config' },
    { label: 'Insightful Team ID', value: record.insightful_team_id, category: 'config' },
    { label: 'Match Score', value: record.match_score, category: 'config' },
  ].filter(f => f.value !== '—' && f.value !== null && f.value !== undefined);
}

function inspectApplication(record) {
  const app = record.applicant || record;
  return [
    { label: 'App ID', value: record.id, category: 'identity', copyable: true },
    { label: 'Applicant', value: app.name || record.applicant_id, category: 'identity' },
    { label: 'Applicant ID', value: truncateId(record.applicant_id), category: 'identity', copyable: true, fullValue: record.applicant_id },
    { label: 'Job ID', value: truncateId(record.job_id), category: 'identity', copyable: true, fullValue: record.job_id },
    { label: 'Org ID', value: truncateId(record.org_id), category: 'identity', copyable: true, fullValue: record.org_id },

    { label: 'Stage', value: record.stage, category: 'status' },
    { label: 'Decision', value: record.decision, category: 'status' },
    { label: 'Source', value: record.source, category: 'status' },
    { label: 'Environment', value: record.environment, category: 'status' },

    { label: 'Applied', value: formatDate(record.applied_at), category: 'timestamps' },
    { label: 'Decision Date', value: formatDate(record.decision_date), category: 'timestamps' },

    { label: 'Resume', value: formatValue(record.resume_submitted), category: 'config' },
    { label: 'Assessment', value: formatValue(record.domain_assessment_passed), category: 'config' },
    { label: 'Skills Eval', value: formatValue(record.skills_evaluation_passed), category: 'config' },
    { label: 'Work Auth', value: formatValue(record.work_auth_verified), category: 'config' },
  ].filter(f => f.value !== '—' && f.value !== null && f.value !== undefined);
}

function inspectAssignment(record) {
  return [
    { label: 'ID', value: record.id, category: 'identity', copyable: true },
    { label: 'Individual ID', value: truncateId(record.individual_id), category: 'identity', copyable: true, fullValue: record.individual_id },
    { label: 'Job ID', value: truncateId(record.job_id), category: 'identity', copyable: true, fullValue: record.job_id },
    { label: 'Org ID', value: truncateId(record.org_id), category: 'identity', copyable: true, fullValue: record.org_id },

    { label: 'Status', value: record.status, category: 'status' },
    { label: 'Environment', value: record.environment, category: 'status' },
    { label: 'Pay Confirmed', value: formatValue(record.pay_confirmed), category: 'status' },

    // R16 LOW-2 audit fix: null-check instead of `||` so a legit rate=0
    // (volunteer / pro-bono) renders "$0/hr" rather than being silently
    // dropped by the `.filter(f => f.value !== '—' && ...)` at the end of
    // the returned array literal. Sibling to R15 LOW-1's Admin.jsx God
    // Mode forensic panel fix. Same class of bug.
    // R19 LOW audit fix: filter-reference instead of line number.
    { label: 'Rate', value: record.rate != null ? `$${record.rate}/hr` : '—', category: 'metrics' },
    { label: 'Confirmed Rate', value: record.confirmed_rate != null ? `$${record.confirmed_rate}/hr` : '—', category: 'metrics' },
    { label: 'Hours Logged', value: record.hours_logged, category: 'metrics' },
    { label: 'Quality', value: record.quality_score ? `${record.quality_score}%` : '—', category: 'metrics' },
    { label: 'Annotations', value: record.annotations_completed, category: 'metrics' },
    { label: 'Total Earned', value: record.total_earned ? `$${record.total_earned}` : '—', category: 'metrics' },

    { label: 'Assigned', value: formatDate(record.assigned_at), category: 'timestamps' },
    { label: 'Started', value: formatDate(record.started_at), category: 'timestamps' },
    { label: 'Completed', value: formatDate(record.completed_at), category: 'timestamps' },
    { label: 'Pay Confirmed At', value: formatDate(record.pay_confirmed_at), category: 'timestamps' },
    { label: 'Pay Confirmed By', value: record.pay_confirmed_by, category: 'timestamps' },
  ].filter(f => f.value !== '—' && f.value !== null && f.value !== undefined);
}

// ─── Main Export ───────────────────────────────────────────

const INSPECTORS = {
  annotator: inspectAnnotator,
  job: inspectJob,
  application: inspectApplication,
  assignment: inspectAssignment,
};

/**
 * Inspect any entity record and return structured field data.
 * @param {string} type — 'annotator' | 'job' | 'application' | 'assignment'
 * @param {Object} record — the raw DB record
 * @returns {Array<{ label, value, category, copyable?, fullValue? }>}
 */
export function inspectEntity(type, record) {
  if (!record) return [];
  const inspector = INSPECTORS[type];
  if (!inspector) {
    // Generic fallback: dump all fields
    return Object.entries(record)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => ({ label: k, value: formatValue(v), category: 'raw' }));
  }
  return inspector(record);
}

/**
 * Get a short summary for tooltips.
 * @returns {{ type, id, name, timestamp }}
 */
export function getEntitySummary(type, record) {
  if (!record) return null;
  return {
    type,
    id: record.id,
    name: record.name || record.title || record.stage || `${type} record`,
    timestamp: record.created_at || record.applied_at || record.assigned_at,
    status: record.status || record.stage || record.decision,
    domain: record.domain || record.primary_domain,
    environment: record.environment,
  };
}

export { truncateId, formatDate, formatValue };
